// Live per-section completion for the publisher intake form.
//
// Derived from validateIntakePayload so "done" and "will submit" can never
// disagree: a section is complete when its required inputs are present AND
// none of its validation rules fire. Purely presentational — submit still runs
// the full validator.

import type { IntakeValues } from '../../../types/publisherIntake';
import { validateIntakePayload } from './validateIntakePayload';

export type IntakeSectionKey =
  | 'station'
  | 'brand'
  | 'footage'
  | 'pricing'
  | 'adops'
  | 'team';

const SECTION_ERROR_KEYS: Record<IntakeSectionKey, string[]> = {
  station: ['slug', 'name'],
  brand: ['logoUrl', 'primaryColor', 'secondaryColor', 'accentColor'],
  footage: [],
  pricing: ['ronCpm'],
  adops: ['adOpsEmail', 'adOpsPhone'],
  team: ['teamMembers', 'teamCommissionPercent'],
};

/**
 * The sections a set of validation errors belongs to. Lets the submit handler
 * jump straight to a lone offending section instead of the top summary.
 */
export function sectionsWithErrors(
  errors: Record<string, string | undefined>,
): IntakeSectionKey[] {
  const hit: IntakeSectionKey[] = [];
  for (const section of Object.keys(SECTION_ERROR_KEYS) as IntakeSectionKey[]) {
    const owns = Object.keys(errors).some((key) =>
      SECTION_ERROR_KEYS[section].some((p) => key === p || key.startsWith(`${p}.`)),
    );
    if (owns) hit.push(section);
  }
  return hit;
}

export function sectionCompletion(
  values: IntakeValues,
  slugTaken = false,
): Record<IntakeSectionKey, boolean> {
  const errors = validateIntakePayload(values, slugTaken);
  // Repeater rows key errors as `teamMembers.0.email`, so prefix-match too.
  const clean = (section: IntakeSectionKey) =>
    !Object.keys(errors).some((key) =>
      SECTION_ERROR_KEYS[section].some((p) => key === p || key.startsWith(`${p}.`)),
    );

  return {
    station: Boolean(values.slug.trim()) && Boolean(values.name.trim()) && clean('station'),
    brand:
      Boolean(values.logoUrl.trim()) && Boolean(values.primaryColor.trim()) && clean('brand'),
    // Optional sections: green means "you gave us this", never "nothing
    // required here", so an empty section stays neutral.
    footage: values.backgroundClips.length > 0,
    pricing: Boolean(values.ronCpm.trim()) && clean('pricing'),
    adops: Boolean(values.adOpsEmail.trim()) && clean('adops'),
    // Optional like footage; rows that exist must still validate, and an
    // added-then-abandoned empty row doesn't count as a team.
    team:
      values.teamMembers.some((m) => m.name.trim() || m.email.trim())
      && clean('team'),
  };
}
